import * as Papa from 'papaparse';
import type { Curve, ParsedData } from '../../../interfaces/toolData';
import type { WaveFileType } from '../types';
import { cleanTimeSeriesData } from '../../dataQuality';

/**
 * Parser for Eclipse Touch decode export CSV files
 * 
 * Key characteristics:
 * - Key-value format: Symbol column contains curve names, Value column contains measurements
 * - DateTime column provides timestamps
 * - Per-point metadata: Confidence, PAvP, Depth stored as separate curves
 * - Wide variety of measurement symbols with different units
 */
export class EclipseTouchParser {
  /**
   * Check if content appears to be an Eclipse Touch decode export file
   */
  static canParse(fileContent: string): boolean {
    const lines = fileContent.split('\n');
    
    // Check header signature
    const header = lines[0]?.trim();
    if (!header) return false;
    
    // Look for the specific Eclipse Touch header pattern
    const expectedColumns = ['DateTime', 'Depth', 'Symbol', 'Value', 'Confidence', 'OSNR', 'ISNR', 'PAvP'];
    const actualColumns = header.split(',').map(col => col.trim());
    
    // Check if all expected columns are present
    return expectedColumns.every(expected => 
      actualColumns.some(actual => actual === expected)
    );
  }

  /**
   * Parse Eclipse Touch decode export file content
   */
  static parse(fileContent: string): ParsedData {
    if (!fileContent.trim()) {
      throw new Error('File is empty');
    }
    
    if (!this.canParse(fileContent)) {
      throw new Error('File does not appear to be an Eclipse Touch decode export');
    }

    // Parse CSV content
    const parseResult = Papa.parse(fileContent, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false, // Keep as strings for proper handling
      transformHeader: (header: string) => header.trim() // Trim spaces from column names
    });

    if (parseResult.errors.length > 0) {
      // Only warn about non-field-mismatch errors to avoid noise from trailing commas
      const significantErrors = parseResult.errors.filter(error => 
        error.type !== 'FieldMismatch' || !error.message?.includes('Too few fields')
      );
      if (significantErrors.length > 0) {
        console.warn('CSV parsing warnings:', significantErrors);
      }
    }

    const rawData = parseResult.data as Record<string, string>[];
    
    if (rawData.length === 0) {
      throw new Error('No data rows found in Eclipse Touch file');
    }

    // Validate required columns
    const requiredColumns = ['DateTime', 'Symbol', 'Value'];
    const firstRow = rawData[0];
    const availableColumns = Object.keys(firstRow);
    
    for (const col of requiredColumns) {
      if (!(col in firstRow)) {
        throw new Error(`Missing required column: ${col}. Available columns: ${availableColumns.join(', ')}`);
      }
    }

    // Parse data into time series format
    const timeSeriesData = this.parseTimeSeriesData(rawData);
    
    if (timeSeriesData.length === 0) {
      throw new Error('No valid time series data found in Eclipse Touch file');
    }

    // Generate curves from time series data
    const curves = this.generateCurves(timeSeriesData);
    
    // Extract metadata
    const metadata = this.extractMetadata(rawData);

    return {
      toolType: 'Eclipse Touch',
      fileType: 'EM_ECLIPSE_TOUCH_DECODE' as WaveFileType,
      curves,
      metadata,
      dataPreview: {
        totalCount: rawData.length,
        first5: rawData.slice(0, 5),
        last5: rawData.slice(-5)
      }
    };
  }

  /**
   * Parse raw CSV data into structured time series format
   */
  private static parseTimeSeriesData(rawData: Record<string, string>[]): Array<{
    timestamp: Date,
    symbol: string,
    value: number,
    confidence?: number,
    pavp?: number,
    depth?: number
  }> {
    const timeSeriesData: Array<{
      timestamp: Date,
      symbol: string, 
      value: number,
      confidence?: number,
      pavp?: number,
      depth?: number
    }> = [];

    for (const row of rawData) {
      // Parse timestamp
      const dateStr = row.DateTime?.trim();
      if (!dateStr) continue;
      
      const timestamp = new Date(dateStr);
      if (isNaN(timestamp.getTime())) {
        console.warn(`Invalid timestamp: ${dateStr}`);
        continue;
      }

      // Parse symbol and value
      const symbol = row.Symbol?.trim();
      const valueStr = row.Value?.trim();
      
      if (!symbol || !valueStr) continue;
      
      const value = parseFloat(valueStr);
      if (isNaN(value)) {
        console.warn(`Invalid value for symbol ${symbol}: ${valueStr}`);
        continue;
      }

      // Parse optional metadata
      const confidence = row.Confidence ? parseFloat(row.Confidence) : undefined;
      const pavp = row.PAvP ? parseFloat(row.PAvP) : undefined;
      const depth = row.Depth ? parseFloat(row.Depth) : undefined;

      timeSeriesData.push({
        timestamp,
        symbol,
        value,
        confidence: !isNaN(confidence!) ? confidence : undefined,
        pavp: !isNaN(pavp!) ? pavp : undefined,
        depth: !isNaN(depth!) ? depth : undefined
      });
    }

    return timeSeriesData;
  }

  /**
   * Generate curves from parsed time series data
   */
  private static generateCurves(timeSeriesData: Array<{
    timestamp: Date,
    symbol: string,
    value: number,
    confidence?: number,
    pavp?: number,
    depth?: number
  }>): Curve[] {
    const curves: Curve[] = [];
    
    // Group data by symbol
    const symbolGroups = new Map<string, Array<{timestamp: Date, value: number}>>();
    const confidenceData: Array<{timestamp: Date, value: number}> = [];
    const pavpData: Array<{timestamp: Date, value: number}> = [];
    const depthData: Array<{timestamp: Date, value: number}> = [];

    for (const entry of timeSeriesData) {
      // Group main symbol data
      if (!symbolGroups.has(entry.symbol)) {
        symbolGroups.set(entry.symbol, []);
      }
      symbolGroups.get(entry.symbol)!.push({
        timestamp: entry.timestamp,
        value: entry.value
      });

      // Collect metadata curves
      if (entry.confidence !== undefined) {
        confidenceData.push({
          timestamp: entry.timestamp,
          value: entry.confidence
        });
      }

      if (entry.pavp !== undefined) {
        pavpData.push({
          timestamp: entry.timestamp,
          value: entry.pavp
        });
      }

      if (entry.depth !== undefined && entry.depth !== 0) {
        depthData.push({
          timestamp: entry.timestamp,
          value: entry.depth
        });
      }
    }

    // Create curves for each symbol
    for (const [symbol, dataPoints] of symbolGroups) {
      if (dataPoints.length === 0) continue;
      
      // Sort by timestamp
      dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      const unit = this.getSymbolUnit(symbol);
      
      const rawCurve: Curve = {
        name: symbol,
        xValues: dataPoints.map(dp => dp.timestamp),
        yValues: dataPoints.map(dp => dp.value),
        yUnit: unit
      };
      
      // Clean the data
      const cleanupResult = cleanTimeSeriesData(rawCurve);
      if (cleanupResult.cleanedCurve.xValues.length > 0) {
        curves.push(cleanupResult.cleanedCurve);
      }
    }

    // Add metadata curves
    if (confidenceData.length > 0) {
      confidenceData.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      const confCurve: Curve = {
        name: 'Confidence',
        xValues: confidenceData.map(dp => dp.timestamp),
        yValues: confidenceData.map(dp => dp.value),
        yUnit: '%'
      };
      const cleanupResult = cleanTimeSeriesData(confCurve);
      if (cleanupResult.cleanedCurve.xValues.length > 0) {
        curves.push(cleanupResult.cleanedCurve);
      }
    }

    if (pavpData.length > 0) {
      pavpData.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      const pavpCurve: Curve = {
        name: 'PAvP',
        xValues: pavpData.map(dp => dp.timestamp),
        yValues: pavpData.map(dp => dp.value),
        yUnit: 'dB'
      };
      const cleanupResult = cleanTimeSeriesData(pavpCurve);
      if (cleanupResult.cleanedCurve.xValues.length > 0) {
        curves.push(cleanupResult.cleanedCurve);
      }
    }

    if (depthData.length > 0) {
      depthData.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      const depthCurve: Curve = {
        name: 'Depth',
        xValues: depthData.map(dp => dp.timestamp),
        yValues: depthData.map(dp => dp.value),
        yUnit: 'ft'
      };
      const cleanupResult = cleanTimeSeriesData(depthCurve);
      if (cleanupResult.cleanedCurve.xValues.length > 0) {
        curves.push(cleanupResult.cleanedCurve);
      }
    }

    return curves;
  }

  /**
   * Get the appropriate unit for a symbol
   */
  private static getSymbolUnit(symbol: string): string {
    const unitMap: Record<string, string> = {
      // Tool face and orientation measurements
      'mtfa': 'degrees',      // Tool face azimuth
      'azm': 'degrees',       // Azimuth
      'inc': 'degrees',       // Inclination
      'cazm': 'degrees',      // Calculated azimuth
      'cinc': 'degrees',      // Calculated inclination
      
      // Dip measurements
      'dipa': 'degrees',      // Dip angle
      'dipw': 'degrees',      // Dip weight/factor
      
      // Gamma measurements
      'gama': 'API',          // Gamma
      
      // Gravity measurements
      'grav': 'g',            // Gravity
      'grvw': 'g',            // Gravity weight/factor
      
      // Magnetic measurements
      'magf': 'gauss',        // Magnetic field
      'magw': 'gauss',        // Magnetic weight/factor
      
      // Battery and power
      'batv': 'V',            // Battery voltage
      'bat2': 'V',            // Battery 2 voltage
      'batw': 'V',            // Battery weight/factor
      
      // Temperature
      'temp': '°C',           // Temperature
      
      // Status and sync
      'sync': '',             // Sync status
      'ssn': '',              // Session number
      'tsn': ''               // Transaction sequence number
    };
    
    const unit = unitMap[symbol.toLowerCase()];
    
    if (!unit) {
      console.warn(`Unknown Eclipse Touch symbol: "${symbol}". Using default unit.`);
      return 'units';
    }
    
    return unit;
  }

  /**
   * Extract file-level metadata
   */
  private static extractMetadata(rawData: Record<string, string>[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    metadata['Total Records'] = rawData.length.toString();
    
    // Extract time range
    const timestamps = rawData
      .map(row => new Date(row.DateTime))
      .filter(date => !isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    
    if (timestamps.length > 0) {
      metadata['Start Time'] = timestamps[0].toISOString();
      metadata['End Time'] = timestamps[timestamps.length - 1].toISOString();
      
      const duration = timestamps[timestamps.length - 1].getTime() - timestamps[0].getTime();
      metadata['Duration (minutes)'] = (duration / (1000 * 60)).toFixed(2);
    }
    
    // Count unique symbols
    const uniqueSymbols = new Set(rawData.map(row => row.Symbol).filter(Boolean));
    metadata['Unique Symbols'] = uniqueSymbols.size.toString();
    metadata['Symbol List'] = Array.from(uniqueSymbols).sort().join(', ');
    
    return metadata;
  }
}
